/*
 * The floating offers bar (boards SF 01, SF 03, SF 04; GUARDRAILS G9):
 * which offers it shows, in which order, and what each card says. Pure,
 * so it is tested without a page.
 */
import type { Promotion } from '../types/promotion.types';
import { summaryLine } from './promotionTerms';

export type BarAction = 'claim' | 'auto' | 'yours' | 'claimed' | 'applied';

/** A claimed card's line (SF 01b): the same message as the details sheet. */
export const CLAIMED_LINE = 'Claimed · off your bill at the salon';

export interface BarCard {
  id: number;
  icon: string;
  title: string;
  line: string;
  action: BarAction;
}

interface Options {
  /** The location being booked, by name; none chosen yet → every offer. */
  storeName?: string | null;
  /** Today, yyyy-MM-dd, on the salon's calendar. */
  today: string;
  /** The services step: an offer theirs without a claim that fits any day reads "Applied"; a claimed one "Claimed" (SF 04). */
  onServices?: boolean;
}

const sameName = (a?: string | null, b?: string | null) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const firstLower = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : s);

export function barCards(catalog: Promotion[], mine: Promotion[], o: Options): BarCard[] {
  const own = new Map(mine.map(p => [p.id, p]));
  const live = catalog.filter(
    p =>
      // Whole-booking offers only: a deal on services applies by itself, and its
      // tag is on the services it names (SF 88–94, mpgadmin G138).
      !p.isServiceDeal &&
      p.showOnPage &&
      p.startsOn.slice(0, 10) <= o.today &&
      (!p.endsOn || p.endsOn.slice(0, 10) >= o.today) &&
      (p.allLocations || !p.locationNames?.length || !o.storeName || p.locationNames.some(n => sameName(n, o.storeName)))
  );

  const cards = live.map((p): BarCard & { yours: boolean } => {
    const m = own.get(p.id);
    const base = { id: p.id, icon: p.icon, yours: !!m?.yours };
    if (m?.yours) {
      // Claimed (SF 01b, and SF 04 on the services step): the green chip in the place of Claim.
      // The reason still wins the line.
      if (m.claimed) return { ...base, title: p.title, line: m.reason || CLAIMED_LINE, action: 'claimed' };
      // Theirs with no claim needed: only an offer with no date rule fits any day, so only it reads
      // "Applied" before a day is picked; and a reason means it wouldn't apply today ("All 3 used"),
      // so never "Applied" then. The bar draws a line tick after its title.
      if (o.onServices && !p.rebookWithinWeeks && !m.reason)
        return { ...base, title: p.title, line: m.usesText ? `Applied · ${firstLower(m.usesText)}` : 'Applied', action: 'applied' };
      if (m.reason) return { ...base, title: p.title, line: m.reason, action: 'yours' };
      return { ...base, title: p.title, line: m.usesText ? `Yours · ${m.usesText}` : 'Yours', action: 'yours' };
    }
    if (p.noClaimNeeded)
      return { ...base, title: p.title, line: `Applies when it fits · ${summaryLine(p)}`, action: 'auto' };
    return { ...base, title: p.title, line: summaryLine(p), action: 'claim' };
  });

  // Theirs first (the one applying leads), then the rest in the salon's order.
  const rank = (c: { action: BarAction; yours: boolean }) => (c.action === 'applied' ? 0 : c.yours ? 1 : 2);
  return cards
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map(({ c: { yours, ...card } }) => card);
}
